// Wholly-local query leaves that don't belong to a larger domain namespace.
// Each previously lived inline inside a single component; lifted here so the
// cache key + fetch derive from one place (react-query-options-migration).
// Wave 2 repoints the components (SignupForm, DevPersonaSwitcher,
// ShareEntityModal) and onboarding/progress.ts onto these leaves.

import { queryOptions } from '@tanstack/react-query';
import type {
  AttachableInputKind,
  AttachedEntityView,
} from '@leanin/shared';
import type { components } from '@leanin/api-client';
import { getApiClient } from '../api-client';
import { throwApiError } from '../api-error';
import { fetchInvitePreview } from '@/components/onboarding/pending-invite';
import { STEP_CONTENT_BY_KEY } from '@/components/onboarding/checklist-registry';

export type SeedMember = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  photoStorageKey: string | null;
  platformRole: 'admin' | 'moderator' | null;
};

export type OnboardingProgress = components['schemas']['OnboardingProgress'];

export const miscKeys = {
  // Signup invite preview. `token` is the only input and is in the key.
  // `enabled` is the consumer's concern (only fires for invited signups) so
  // it stays at the call site (decision 7).
  invitePreview: (token: string | undefined) =>
    queryOptions({
      queryKey: ['invitePreview', token] as const,
      queryFn: () => fetchInvitePreview(token ?? ''),
      staleTime: 60_000,
    }),

  // Dev-only seeded members for the persona switcher. No params.
  devSeedMembers: () =>
    queryOptions({
      queryKey: ['dev', 'seed-members'] as const,
      queryFn: fetchDevSeedMembers,
      staleTime: 60_000,
    }),

  // Share-modal entity resolution. `kind` + `id` are the inputs and both are
  // in the key. `enabled` (only fires while the modal is open) stays at the
  // call site (decision 7).
  shareResolveAttachment: (kind: AttachableInputKind, id: string) =>
    queryOptions({
      queryKey: ['share', 'resolve-attachment', kind, id] as const,
      queryFn: () => fetchResolvedAttachment(kind, id),
      staleTime: 60_000,
    }),

  // Onboarding progress. No params.
  onboardingProgress: () =>
    queryOptions({
      queryKey: ['onboarding', 'progress'] as const,
      queryFn: fetchOnboardingProgress,
    }),
};

// ---- Client fetchers ----

async function fetchDevSeedMembers(): Promise<readonly SeedMember[]> {
  const { data, response } = await getApiClient().GET(
    '/api/v1/dev/seed-members',
  );
  if (data === undefined) {
    throw new Error(
      `dev-login: failed to fetch seed members (${response.status})`,
    );
  }
  return data.members;
}

async function fetchResolvedAttachment(
  kind: AttachableInputKind,
  id: string,
): Promise<AttachedEntityView | null> {
  const client = getApiClient();
  const { data, error } = await client.POST(
    '/api/v1/posts/attachments/resolve',
    { body: { kind, id } },
  );
  if (error !== undefined || data === undefined) {
    throw new Error('Could not resolve entity for share.');
  }
  return data.attachment ?? null;
}

async function fetchOnboardingProgress(): Promise<OnboardingProgress> {
  const client = getApiClient();
  const { data, error } = await client.GET('/api/v1/me/onboarding-progress');
  if (data === undefined) throwApiError(error);
  // The server's key enum can be ahead of this bundle (additive API change,
  // stale tab). Keys the registry doesn't know would crash every
  // `STEP_CONTENT_BY_KEY[key]` consumer — drop them here so downstream code
  // trusts the type.
  const known = (key: string): boolean => Object.hasOwn(STEP_CONTENT_BY_KEY, key);
  return {
    ...data,
    steps: data.steps.filter(known),
    actions: data.actions.filter((a) => known(a.key)),
  };
}
